import { useTranslation } from 'react-i18next'
import { RemoveIcon } from './icons'
import type { FormState, Override } from './types'

interface OverridesProps {
	form: FormState
	addOverride: () => void
	removeOverride: (idx: number) => void
	updateOverride: (idx: number, field: keyof Override, value: string | boolean | null) => void
}

export function Overrides({ form, addOverride, removeOverride, updateOverride }: OverridesProps) {
	const { t } = useTranslation('common')

	return (
		<section className="flex flex-col gap-3">
			<div className="flex items-center justify-between">
				<div>
					<h2 className="text-sm font-medium text-grey-700 uppercase tracking-wide">
						{t('account.availability.overrides.title')}
					</h2>
					<p className="text-xs text-grey-400 mt-0.5">
						{t('account.availability.overrides.description')}
					</p>
				</div>
			</div>
			{form.schedule.overrides.length === 0 && (
				<p className="text-sm text-grey-400 italic">
					{t('account.availability.overrides.none')}
				</p>
			)}
			<div className="flex flex-col gap-3">
				{form.schedule.overrides.map((override, idx) => (
					<div
						key={idx}
						className="rounded-lg border border-grey-100 bg-grey-50 p-4 flex flex-col gap-3"
					>
						<div className="flex items-center justify-between">
							<div className="flex items-center gap-2 flex-wrap">
								<input
									type="date"
									value={override.from}
									onChange={(e) => updateOverride(idx, 'from', e.target.value)}
									className="rounded border border-grey-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
								/>
								<span className="text-sm text-grey-400">
									{t('account.availability.overrides.date_range_to')}
								</span>
								<input
									type="date"
									value={override.to}
									onChange={(e) => updateOverride(idx, 'to', e.target.value)}
									className="rounded border border-grey-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
								/>
							</div>
							<button
								type="button"
								onClick={() => removeOverride(idx)}
								className="p-1 text-grey-400 hover:text-red-500 transition-colors"
								aria-label="Remove override"
							>
								<RemoveIcon />
							</button>
						</div>
						<div className="flex items-center gap-4">
							<label className="flex items-center gap-2 cursor-pointer">
								<input
									type="radio"
									name={`override-mode-${idx}`}
									checked={!override.open}
									onChange={() => updateOverride(idx, 'open', false)}
									className="accent-brand-600"
								/>
								<span className="text-sm text-grey-700">
									{t('account.availability.overrides.mode_unavailable')}
								</span>
							</label>
							<label className="flex items-center gap-2 cursor-pointer">
								<input
									type="radio"
									name={`override-mode-${idx}`}
									checked={override.open}
									onChange={() => updateOverride(idx, 'open', true)}
									className="accent-brand-600"
								/>
								<span className="text-sm text-grey-700">
									{t('account.availability.overrides.mode_available')}
								</span>
							</label>
						</div>
						<div className="flex flex-col gap-1">
							<span className="text-xs text-grey-500">
								{override.open
									? t('account.availability.overrides.hours_label_available')
									: t('account.availability.overrides.hours_label_unavailable')}
							</span>
							<div className="flex items-center gap-2">
								<input
									type="time"
									value={override.start ?? ''}
									onChange={(e) => updateOverride(idx, 'start', e.target.value || null)}
									className="rounded border border-grey-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
								/>
								<span className="text-sm text-grey-400">–</span>
								<input
									type="time"
									value={override.end ?? ''}
									onChange={(e) => updateOverride(idx, 'end', e.target.value || null)}
									className="rounded border border-grey-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
								/>
							</div>
						</div>
					</div>
				))}

				<button
					type="button"
					onClick={addOverride}
					className="inline-block self-start text-xs font-medium text-brand-600 hover:text-brand-700"
				>
					{t('account.availability.overrides.add')}
				</button>
			</div>
		</section>
	)
}
